// ═══════════════════════════════════════════════════════════════
// VOLUNTEEROS — Schedule Builder
// Smart shift assignment tool — select event, date, role, then
// assign volunteers from the roster with conflict detection
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Title, Text, Paper, Group, Select, MultiSelect, Badge, TextInput, Switch,
  SimpleGrid, Stack, ActionIcon, Tooltip, Divider, Loader, Center,
  ThemeIcon,
} from '../../components/gab/GabComponents';

import { notifications } from '../../lib/notifications';
import {
  IconUserPlus, IconTrash, IconAlertTriangle,
  IconCheck, IconSearch, IconUsers,
} from '@tabler/icons-react';
import {
  useVolunteerStore,
  parseExtras,
  type Volunteer,
  type VolEvent,
  type VolAssignment,
} from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Constants ────────────────────────────────────────────────

const SHIFT_ROLES = [
  { value: 'Field', label: 'Field' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Logistics', label: 'Logistics' },
  { value: 'Command', label: 'Command' },
  { value: 'Communications', label: 'Communications' },
  { value: 'Other', label: 'Other' },
];

const ROLE_MATCH_MAP: Record<string, string[]> = {
  Field: ['active', 'both'],
  Medical: ['medical'],
  Logistics: ['office', 'both'],
  Command: ['office', 'both'],
  Communications: ['comms', 'both'],
  Other: [],
};

// ── Helpers ──────────────────────────────────────────────────

function timeToMinutes(t: string): number {
  if (!t) return -1;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function timesOverlap(
  s1: string, e1: string, s2: string, e2: string,
): boolean {
  const a0 = timeToMinutes(s1), a1 = timeToMinutes(e1);
  const b0 = timeToMinutes(s2), b1 = timeToMinutes(e2);
  if (a0 < 0 || a1 < 0 || b0 < 0 || b1 < 0) return false;
  return a0 < b1 && b0 < a1;
}

// ── Component ────────────────────────────────────────────────

export default function ScheduleBuilder() {
  const { canEdit } = useModulePermission('volunteer');
  const { incidents, activeIncident, loadIncidents } = useAuthStore();
  const {
    volunteers, events, assignments,
    loadVolunteers, loadEvents, loadAssignments,
    createAssignment, deleteAssignment,
  } = useVolunteerStore();

  // Initial load
  const didLoad = useRef(false);
  useEffect(() => {
    if (didLoad.current) return;
    didLoad.current = true;
    loadIncidents();
    loadVolunteers();
    loadEvents();
    loadAssignments();
  }, []);

  // Left panel state — auto-select active incident
  const [incidentId, setIncidentId] = useState<string | null>(activeIncident ? String(activeIncident.id) : null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [shiftDate, setShiftDate] = useState<string | null>(null);
  const [shiftRoles, setShiftRoles] = useState<string[]>([]);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');
  const [filterAvailable, setFilterAvailable] = useState(false);

  // Right panel filters
  const [searchText, setSearchText] = useState('');
  const [filterRolePref, setFilterRolePref] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterCert, setFilterCert] = useState<string | null>(null);

  const [assigning, setAssigning] = useState<number | null>(null);

  // Derived data
  const filteredEvents = useMemo(
    () => events.filter(e => !incidentId || String(e.incidentId) === incidentId),
    [events, incidentId],
  );

  const selectedEvent: VolEvent | undefined = useMemo(
    () => events.find(e => String(e.id) === eventId),
    [events, eventId],
  );

  const dateStr = shiftDate || '';

  // Assignments for this event + date
  const eventDateAssignments = useMemo(
    () => assignments.filter(a =>
      eventId && String(a.eventId) === eventId &&
      dateStr && a.shiftDate === dateStr &&
      a.status !== 'cancelled',
    ),
    [assignments, eventId, dateStr],
  );

  // All non-cancelled assignments by volunteer keyed by volunteerId
  const volAssignmentMap = useMemo(() => {
    const map: Record<string, VolAssignment[]> = {};
    for (const a of assignments) {
      if (a.status === 'cancelled') continue;
      const key = a.volunteerId;
      if (!map[key]) map[key] = [];
      map[key].push(a);
    }
    return map;
  }, [assignments]);

  // Already-assigned volunteer IDs for this event+date
  const assignedVolIds = useMemo(
    () => new Set(eventDateAssignments.map(a => a.volunteerId)),
    [eventDateAssignments],
  );

  // Load settings for rest hour enforcement
  const volSettings = useMemo(() => {
    try {
      const raw = localStorage.getItem('vol-org-settings');
      if (raw) return JSON.parse(raw);
    } catch { /* */ }
    return { thresholds: { minRestHours: 8, maxConsecutiveDays: 7 } };
  }, []);

  // Detect conflicts for a given volunteer (time overlap + rest hours)
  const hasConflict = (vol: Volunteer): VolAssignment | null => {
    if (!dateStr || !startTime || !endTime) return null;
    const volAssigns = volAssignmentMap[String(vol.id)] || [];
    for (const a of volAssigns) {
      // Direct time overlap
      if (a.shiftDate === dateStr && timesOverlap(startTime, endTime, a.startTime, a.endTime)) {
        return a;
      }
      // Rest hour check — adjacent days
      if (a.endTime && startTime) {
        const minRest = volSettings.thresholds?.minRestHours || 8;
        const prevEnd = dayjs(`${a.shiftDate}T${a.endTime}`);
        const nextStart = dayjs(`${dateStr}T${startTime}`);
        const restHours = nextStart.diff(prevEnd, 'hour', true);
        if (restHours > 0 && restHours < minRest) {
          return a; // insufficient rest
        }
      }
    }
    return null;
  };

  // Check if volunteer role pref matches any selected shift role
  const isRoleMatch = (vol: Volunteer): boolean => {
    if (shiftRoles.length === 0) return false;
    return shiftRoles.some(role => {
      const matches = ROLE_MATCH_MAP[role] || [];
      return matches.includes(vol.rolePref);
    });
  };

  // Check volunteer availability for the selected date/time
  const isAvailableForShift = (vol: Volunteer): boolean => {
    if (!dateStr || !startTime || !endTime) return true;
    try {
      const avail = JSON.parse(vol.availability || '{}');
      const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const dayOfWeek = dayNames[new Date(dateStr).getDay()];
      const dayAvail = avail[dayOfWeek];
      if (!dayAvail) return false;
      const startHour = parseInt(startTime.split(':')[0]);
      const endHour = parseInt(endTime.split(':')[0]);
      // Check relevant time blocks
      if (startHour < 12 && !dayAvail.morning) return false;
      if (((startHour >= 12 && startHour < 17) || (endHour > 12 && endHour <= 17)) && !dayAvail.afternoon) return false;
      if ((startHour >= 17 || endHour > 17) && !dayAvail.evening) return false;
      return true;
    } catch { return true; }
  };

  // Collect unique certs across all volunteers for filter dropdown
  const allCerts = useMemo(() => {
    const set = new Set<string>();
    for (const v of volunteers) {
      if (v.certs) {
        v.certs.split(',').forEach(c => { const t = c.trim(); if (t) set.add(t); });
      }
    }
    return Array.from(set).sort();
  }, [volunteers]);

  // Filter volunteers for roster
  const filteredVolunteers = useMemo(() => {
    return volunteers.filter(v => {
      if (filterStatus && v.status !== filterStatus) return false;
      if (filterRolePref && v.rolePref !== filterRolePref) return false;
      if (filterCert) {
        const certs = (v.certs || '').split(',').map(c => c.trim().toLowerCase());
        if (!certs.includes(filterCert.toLowerCase())) return false;
      }
      if (filterAvailable && !isAvailableForShift(v)) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const name = `${v.firstName} ${v.lastName}`.toLowerCase();
        if (!name.includes(q) && !(v.email || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [volunteers, filterStatus, filterRolePref, filterCert, filterAvailable, searchText, dateStr, startTime, endTime]);

  // Summary counts
  const assignedCount = eventDateAssignments.length;
  const targetCount = selectedEvent?.targetVol || 0;

  // ── Actions ────────────────────────────────────────────────

  const handleAssign = async (vol: Volunteer) => {
    if (!eventId || !incidentId || shiftRoles.length === 0 || !dateStr) {
      notifications.show({ message: 'Select incident, event, date, and at least one role first', color: '#C68700' });
      return;
    }
    const shiftRole = shiftRoles.join(', ');
    const conflict = hasConflict(vol);
    if (conflict) {
      const evtName = events.find(e => String(e.id) === conflict.eventId)?.name || conflict.eventId;
      notifications.show({
        title: 'Schedule Conflict',
        message: `${vol.firstName} ${vol.lastName} already has an overlapping shift on ${conflict.shiftDate} (${conflict.startTime}-${conflict.endTime}) for ${evtName}`,
        color: '#F86A0B',
        autoClose: 6000,
      });
    }
    setAssigning(vol.id);
    try {
      const extras = parseExtras('');
      const notesJson = JSON.stringify({
        ...extras,
        volunteerName: `${vol.firstName} ${vol.lastName}`,
        eventName: selectedEvent?.name || '',
      });
      await createAssignment({
        volunteerId: String(vol.id),
        eventId,
        incidentId,
        role: shiftRole,
        status: 'scheduled',
        shiftDate: dateStr,
        startTime,
        endTime,
        checkIn: '',
        checkOut: '',
        hours: '',
        notes: notesJson,
      });
      notifications.show({ message: `Assigned ${vol.firstName} ${vol.lastName}`, color: 'var(--color-success)' });
    } catch (_err) {
      notifications.show({ message: 'Failed to assign volunteer', color: 'var(--color-error)' });
    } finally {
      setAssigning(null);
    }
  };

  const handleRemove = async (a: VolAssignment) => {
    try {
      await deleteAssignment(a.id);
      notifications.show({ message: 'Assignment removed', color: '#939598' });
    } catch {
      notifications.show({ message: 'Failed to remove assignment', color: 'var(--color-error)' });
    }
  };

  // ── Render ─────────────────────────────────────────────────

  const loading = useVolunteerStore(s => s.loading);
  if (loading && volunteers.length === 0) {
    return <Center py="xl"><Loader size="lg" /></Center>;
  }

  return (
    <div>
      <Title order={3} mb="md" c="black">Schedule Builder</Title>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        {/* ─── Left Panel: Shift Setup ─── */}
        <Stack gap="md">
          <Paper p="md" radius="sm" withBorder>
            <Text fw={600} size="sm" mb="sm">Shift Configuration</Text>

            <Select
              label="Incident"
              placeholder="Select incident"
              data={incidents.map(i => ({ value: String(i.id || ''), label: String(i.name || 'Unknown Incident') }))}
              value={incidentId}
              onChange={(v) => { setIncidentId(v); setEventId(null); }}
              clearable
              mb="sm"
            />

            <Select
              label="Event"
              placeholder="Select event"
              data={filteredEvents.map(e => ({ value: String(e.id || ''), label: String(e.name || e.eventName || `Event #${e.id}`) }))}
              value={eventId}
              onChange={(v) => {
                setEventId(v);
                // Auto-fill shift date from event start date
                if (v) {
                  const ev = events.find(e => String(e.id) === v);
                  if (ev?.startDate && !shiftDate) {
                    setShiftDate(dayjs(ev.startDate).format('YYYY-MM-DD'));
                  }
                }
              }}
              clearable
              searchable
              disabled={!incidentId}
              mb="sm"
            />

            <TextInput
              label="Shift Date"
              type="date"
              value={shiftDate || ''}
              onChange={e => setShiftDate(e.target.value || null)}
              mb="sm"
            />

            <MultiSelect
              label="Shift Roles"
              placeholder="Select one or more roles"
              data={SHIFT_ROLES}
              value={shiftRoles}
              onChange={setShiftRoles}
              clearable
              mb="sm"
            />

            <Group grow>
              <TextInput
                label="Start Time"
                type="time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
              />
              <TextInput
                label="End Time"
                type="time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
              />
            </Group>
          </Paper>

          {/* Summary */}
          {selectedEvent && dateStr && (
            <Paper p="md" radius="sm" withBorder>
              <Group justify="space-between" mb="xs">
                <Text fw={600} size="sm">Shift Summary</Text>
                <Badge
                  color={assignedCount >= targetCount && targetCount > 0 ? 'var(--color-success)' : '#F86A0B'}
                  variant="light"
                >
                  {assignedCount} / {targetCount || '?'} assigned
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {selectedEvent.name} &middot; {dateStr} &middot; {shiftRoles.length > 0 ? shiftRoles.join(', ') : 'No role'} &middot; {startTime}&ndash;{endTime}
              </Text>
            </Paper>
          )}

          {/* Currently assigned for this event+date */}
          {eventDateAssignments.length > 0 && (
            <Paper p="md" radius="sm" withBorder>
              <Text fw={600} size="sm" mb="sm">
                Assigned ({eventDateAssignments.length})
              </Text>
              <Stack gap="xs">
                {eventDateAssignments.map(a => {
                  const vol = volunteers.find(v => String(v.id) === a.volunteerId);
                  const extras = parseExtras(a.notes);
                  const name = vol
                    ? `${vol.firstName} ${vol.lastName}`
                    : (extras.volunteerName as string) || `Vol #${a.volunteerId}`;
                  return (
                    <Group key={a.id} justify="space-between" style={{ fontSize: 14 }}>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>{name}</Text>
                        <Badge size="xs" variant="light">{a.role}</Badge>
                        <Text size="xs" c="dimmed">{a.startTime}&ndash;{a.endTime}</Text>
                      </Group>
                      {canEdit && (
                        <Tooltip label="Remove assignment">
                          <ActionIcon
                            variant="subtle"
                            color="var(--color-error)"
                            size="sm"
                            onClick={() => handleRemove(a)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </Stack>

        {/* ─── Right Panel: Volunteer Roster ─── */}
        <Stack gap="md">
          <Paper p="md" radius="sm" withBorder>
            <Group justify="space-between" mb="sm">
              <Group gap="xs">
                <IconUsers size={16} />
                <Text fw={600} size="sm">Volunteer Roster</Text>
              </Group>
              <Badge variant="light">{filteredVolunteers.length} volunteers</Badge>
            </Group>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" mb="sm">
              <TextInput
                placeholder="Search name or email"
                leftSection={<IconSearch size={14} />}
                size="xs"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
              <Select
                placeholder="Role preference"
                data={[
                  { value: 'active', label: 'Active / Field' },
                  { value: 'office', label: 'Office' },
                  { value: 'medical', label: 'Medical' },
                  { value: 'comms', label: 'Communications' },
                  { value: 'both', label: 'Both' },
                ]}
                value={filterRolePref}
                onChange={setFilterRolePref}
                clearable
                size="xs"
              />
              <Select
                placeholder="Status"
                data={[
                  { value: 'active', label: 'Active' },
                  { value: 'probationary', label: 'Probationary' },
                  { value: 'inactive', label: 'Inactive' },
                ]}
                value={filterStatus}
                onChange={setFilterStatus}
                clearable
                size="xs"
              />
              {allCerts.length > 0 && (
                <Select
                  placeholder="Certification"
                  data={allCerts.map(c => ({ value: c, label: c }))}
                  value={filterCert}
                  onChange={setFilterCert}
                  clearable
                  size="xs"
                />
              )}
            </SimpleGrid>
            {dateStr && (
              <Switch
                label="Only show available volunteers"
                size="xs"
                checked={filterAvailable}
                onChange={e => setFilterAvailable(e.currentTarget.checked)}
                mb="xs"
              />
            )}

            <Divider mb="sm" />

            <div style={{ maxHeight: 480, overflowY: 'auto' }}>

            {/* Status message */}
            {(!eventId || !dateStr || shiftRoles.length === 0) && (
              <Paper p="sm" bg="yellow.0" radius="sm" mb="sm" style={{ border: '1px solid rgba(198,135,0,0.28)' }}>
                <Text size="xs" c="yellow.8">
                  {!eventId ? 'Select an incident and event to start assigning.' :
                   !dateStr ? 'Set a shift date.' :
                   'Select at least one shift role.'}
                </Text>
              </Paper>
            )}

            {/* Volunteer list */}
            <Stack gap={4}>
              {filteredVolunteers.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="md">No volunteers match filters.</Text>
              )}
              {filteredVolunteers.map(vol => {
                const alreadyAssigned = assignedVolIds.has(String(vol.id));
                const conflict = hasConflict(vol);
                const roleMatch = isRoleMatch(vol);
                const certs = vol.certs ? vol.certs.split(',').map(c => c.trim()).filter(Boolean) : [];

                return (
                  <Paper
                    key={vol.id}
                    p="xs"
                    radius="sm"
                    withBorder
                    style={{
                      borderColor: alreadyAssigned
                        ? 'rgba(3,119,48,0.45)'
                        : roleMatch
                          ? 'rgba(75,63,255,0.28)'
                          : undefined,
                      opacity: alreadyAssigned ? 0.6 : 1,
                      cursor: alreadyAssigned || !eventId || !dateStr || shiftRoles.length === 0 || !canEdit ? 'default' : 'pointer',
                    }}
                    onClick={() => {
                      if (!alreadyAssigned && eventId && dateStr && shiftRoles.length > 0 && canEdit) {
                        handleAssign(vol);
                      }
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm" fw={600} truncate>
                            {vol.firstName} {vol.lastName}
                          </Text>
                          {roleMatch && (
                            <Tooltip label="Role preference matches">
                              <ThemeIcon size="xs" variant="light" color="var(--color-primary-700)">
                                <IconCheck size={10} />
                              </ThemeIcon>
                            </Tooltip>
                          )}
                          {conflict && !alreadyAssigned && (
                            <Tooltip label={`Conflict: ${conflict.startTime}-${conflict.endTime} on ${conflict.shiftDate}`}>
                              <ThemeIcon size="xs" variant="light" color="#F86A0B">
                                <IconAlertTriangle size={10} />
                              </ThemeIcon>
                            </Tooltip>
                          )}
                        </Group>
                        <Group gap={4} mt={2}>
                          <Badge size="xs" variant="dot" color="#939598">{vol.rolePref}</Badge>
                          <Badge size="xs" variant="light">{vol.status}</Badge>
                          {certs.slice(0, 3).map(c => (
                            <Badge key={c} size="xs" variant="outline" color="#03AC9E">{c}</Badge>
                          ))}
                          {certs.length > 3 && (
                            <Text size="xs" c="dimmed">+{certs.length - 3}</Text>
                          )}
                        </Group>
                      </div>

                      {alreadyAssigned ? (
                        <Badge size="sm" color="var(--color-success)" variant="filled">Assigned</Badge>
                      ) : canEdit ? (
                        <Tooltip label={!eventId ? 'Select an event first' : !dateStr ? 'Set a shift date' : shiftRoles.length === 0 ? 'Select shift roles' : `Assign ${vol.firstName} to this shift`}>
                          <ActionIcon
                            variant="filled"
                            color="#03AC9E"
                            size="md"
                            loading={assigning === vol.id}
                            onClick={(e) => { e.stopPropagation(); handleAssign(vol); }}
                            disabled={!eventId || shiftRoles.length === 0 || !dateStr}
                          >
                            <IconUserPlus size={16} />
                          </ActionIcon>
                        </Tooltip>
                      ) : null}
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
            </div>
          </Paper>
        </Stack>
      </SimpleGrid>
    </div>
  );
}
